<script setup>
import { ref } from "vue";
import LeadForm from "../ui/LeadForm.vue";
import IconCheck from "../icons/IconCheck.vue";
import IconMail from "../icons/IconMail.vue";
import { site } from "../../content/site";
import { useLanding } from "../../composables/useLanding";
import { useStaggerReveal } from "../../composables/useStaggerReveal";
import { useSpotlight } from "../../composables/useSpotlight";
import { usePauseOffscreen } from "../../composables/usePauseOffscreen";

const content = useLanding().lead;

const list = ref(null);
const { revealed } = useStaggerReveal(list);

const section = ref(null);
const { paused } = usePauseOffscreen(section);
useSpotlight(section);
</script>

<template>
  <section
    ref="section"
    class="section section--ink lead-section"
    :class="{ 'is-paused': paused }"
    id="lead-form"
  >
    <!-- An Inspira grid, and a band of light sweeping slowly across it. -->
    <div class="lead-section__grid bg-grid-grid-on-ink" aria-hidden="true" />
    <div class="lead-section__beam" aria-hidden="true" />

    <div class="container lead-section__content">
      <div class="lead-section__inner">
        <div class="lead-section__copy">
          <span class="eyebrow">בואו נדבר</span>
          <h2 class="lead-section__title">{{ content.title }}</h2>
          <p class="lead-section__text">{{ content.text }}</p>

          <ul ref="list" class="lead-section__points" role="list">
            <li
              v-for="(point, i) in content.points"
              :key="point"
              class="reveal reveal--start"
              :class="{
                'is-revealed': revealed,
                'animate-in fade-in slide-in-from-start-4 fill-mode-both duration-700': revealed,
              }"
              :style="{ '--reveal-index': i, animationDelay: `${i * 110}ms` }"
            >
              <IconCheck aria-hidden="true" />
              <span>{{ point }}</span>
            </li>
          </ul>

          <a :href="`mailto:${site.email}`" class="lead-section__email">
            <IconMail aria-hidden="true" />
            {{ site.email }}
          </a>
        </div>

        <div class="lead-section__card spotlight">
          <LeadForm
            variant="full"
            form-id="footer"
            id-prefix="lead"
            submit-label="שלחו פרטים"
            :business-placeholder="content.businessPlaceholder"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* The brand light rising from behind the form. */
.lead-section {
  position: relative;
  overflow: hidden;
  background: radial-gradient(
      45% 70% at 12% 100%,
      var(--brand-glow-soft),
      transparent 70%
    ),
    radial-gradient(35% 50% at 100% 0%, var(--brand-glow-faint), transparent 70%),
    var(--ink);
}

.lead-section__grid {
  position: absolute;
  inset: 0;
  pointer-events: none;
  -webkit-mask-image: radial-gradient(
    60% 80% at 20% 60%,
    black,
    transparent 80%
  );
  mask-image: radial-gradient(60% 80% at 20% 60%, black, transparent 80%);
}

/*
 * A wide, soft diagonal band that drifts across the section and back.
 * Gradient and transform only.
 */
.lead-section__beam {
  position: absolute;
  inset-block: -20%;
  inset-inline-start: -30%;
  inline-size: 60%;
  pointer-events: none;
  background: linear-gradient(
    105deg,
    transparent 30%,
    rgba(18, 115, 230, 0.16) 50%,
    transparent 70%
  );
  animation: beam 16s var(--ease-in-out) infinite alternate;
}

@keyframes beam {
  to {
    transform: translate3d(140%, 0, 0);
  }
}

.lead-section__content {
  position: relative;
}

.lead-section__inner {
  display: grid;
  gap: var(--space-8);
  align-items: center;
}

.lead-section__title {
  font-size: calc(var(--text-4xl) * var(--display-scale));
  margin-block: var(--space-4) var(--space-3);
  max-inline-size: 20ch;
}

.lead-section__text {
  color: var(--on-ink-muted);
  font-size: var(--text-lg);
  margin-block-end: var(--space-5);
  max-inline-size: 46ch;
}

.lead-section__points {
  display: grid;
  gap: var(--space-3);
  padding: 0;
  margin: 0 0 var(--space-6);
  list-style: none;
}

/* Held back until the list scrolls in, then tw-animate brings each one in. */
.js .lead-section__points li:not(.is-revealed) {
  opacity: 0;
}

.lead-section__points li {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  color: var(--on-ink);
}

/* Each check sits in a small tinted disc. */
.lead-section__points :deep(svg) {
  box-sizing: content-box;
  inline-size: 14px;
  block-size: 14px;
  flex-shrink: 0;
  padding: 4px;
  margin-block-start: 1px;
  border-radius: 50%;
  background: rgba(255, 122, 69, 0.18);
  color: var(--accent);
  stroke-width: 3;
}

.lead-section__email {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--on-ink-muted);
  font-size: var(--text-sm);
  transition: color var(--dur-fast) var(--ease-in-out);
}

.lead-section__email:hover {
  color: var(--on-ink);
}

.lead-section__email :deep(svg) {
  inline-size: 18px;
  block-size: 18px;
}

.lead-section__card {
  position: relative;
  padding: var(--space-6);
  background: var(--surface);
  border-radius: var(--radius-2xl);
  box-shadow: 0 0 0 8px rgba(255, 255, 255, 0.06), var(--lift-3);
}

@media (min-width: 900px) {
  .lead-section__inner {
    grid-template-columns: minmax(0, 1fr) minmax(0, 440px);
    gap: var(--space-9);
  }
}

@media (prefers-reduced-motion: reduce) {
  .lead-section__beam {
    animation: none;
  }
}
</style>
